import { json } from '@remix-run/node'
import { Link, useLoaderData, useParams } from '@remix-run/react'
import { type LoaderFunctionArgs } from '@remix-run/server-runtime'
import {
  type NestedSection,
  nestSections,
} from '#app/models.client/sections/index.js'
import { getSectionsByDay } from '#app/models.server/sections/index.js'

export async function loader({ params }: LoaderFunctionArgs) {
  const { day } = params
  if (!day) {
    throw new Error('Missing day parameter')
  }
  const sections = await getSectionsByDay(day)

  return json({ sections })
}

export function Section({
  day,
  section,
  level,
}: {
  day: string
  section: NestedSection
  level: number
}) {
  const classNames = [
    'my-2 text-lg font-semibold',
    'mx-4 my-2 text-base font-normal',
    'mx-8 my-2 text-sm font-normal',
  ]
  const className = classNames[level] || classNames[classNames.length - 1]
  return (
    <div className={className}>
      <Link to={`/hansard/${day}/section/${section.id}/`}>{section.title}</Link>{' '}
      {section.type && <span className="text-gray-600">({section.type})</span>}
      {section.children?.map(child => (
        <Section key={child.id} day={day} section={child} level={level + 1} />
      ))}
    </div>
  )
}

export function FullOutline({
  day,
  sections,
}: {
  day: string
  sections: NestedSection[]
}) {
  return (
    <div>
      {sections.map(section => (
        <Section key={section.id} section={section} day={day || ''} level={0} />
      ))}
    </div>
  )
}

export default function Overview() {
  const { sections } = useLoaderData<typeof loader>()
  const { day } = useParams()
  if (!day) {
    throw new Error('Missing day parameter')
  }

  const title = new Date(day as string).toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <main className="mx-auto max-w-screen-lg px-8">
      <h1 className="my-8 text-3xl font-bold">{title}</h1>
      <FullOutline day={day} sections={nestSections(sections)} />
    </main>
  )
}
